/**
 * Offline Voice Queue Service
 * Stores voice recordings when offline and syncs when back online
 */
import { get, set, del, entries } from 'idb-keyval';

interface QueuedVoiceMessage {
  id: string;
  userId?: string;
  audioBlob: Blob;
  context: 'question' | 'answer' | 'agent';
  metadata: {
    targetId?: string; // question ID for answers
    category?: string;
    batchDay?: number;
  };
  createdAt: number;
  status: 'pending' | 'processing' | 'failed';
  retryCount: number;
}

const VOICE_QUEUE_PREFIX = 'voice_queue_';

class OfflineVoiceQueueService {
  private isProcessing = false;
  private listeners: Array<(count: number) => void> = [];

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.processQueue());
    }
  }

  async add(
    audioBlob: Blob,
    context: QueuedVoiceMessage['context'],
    metadata: QueuedVoiceMessage['metadata'] = {},
    userId?: string
  ): Promise<string> {
    const id = `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const queuedMessage: QueuedVoiceMessage = {
      id,
      userId,
      audioBlob,
      context,
      metadata,
      createdAt: Date.now(),
      status: 'pending',
      retryCount: 0,
    };

    await set(`${VOICE_QUEUE_PREFIX}${id}`, queuedMessage);
    await this.notifyListeners();

    // Try to process immediately if online
    if (navigator.onLine) {
      this.processQueue();
    }

    return id;
  }

  async remove(id: string): Promise<void> {
    await del(`${VOICE_QUEUE_PREFIX}${id}`);
    await this.notifyListeners();
  }

  async getAll(): Promise<QueuedVoiceMessage[]> {
    const allEntries = await entries<string, QueuedVoiceMessage>();
    return allEntries
      .filter(([key]) => key.startsWith(VOICE_QUEUE_PREFIX))
      .map(([, value]) => value)
      .sort((a, b) => a.createdAt - b.createdAt);
  }

  async getCount(): Promise<number> {
    const all = await this.getAll();
    return all.filter(m => m.status === 'pending').length;
  }

  async processQueue(): Promise<void> {
    if (this.isProcessing || !navigator.onLine) return;

    this.isProcessing = true;

    try {
      const { edgeFunctions } = await import('./edgeFunctions');
      const pending = await this.getAll();

      for (const message of pending.filter(m => m.status === 'pending')) {
        try {
          // Update status to processing
          await set(`${VOICE_QUEUE_PREFIX}${message.id}`, {
            ...message,
            status: 'processing',
          });

          // Transcribe the voice
          const { data: transcription, error: transcribeError } = await edgeFunctions.transcribeVoice(message.audioBlob, localStorage.getItem('i18nextLng') || undefined, message.userId);

          if (transcribeError) {
            throw transcribeError;
          }

          // Handle based on context
          if (message.context === 'agent' && transcription?.transcription?.text && message.userId) {
            await edgeFunctions.agriAgentChat(message.userId, transcription.transcription.text, localStorage.getItem('i18nextLng') || 'en');
          }

          // Remove from queue on success
          await this.remove(message.id);
        } catch (error) {
          // Update retry count
          const newRetryCount = message.retryCount + 1;
          const newStatus: 'pending' | 'failed' = newRetryCount >= 3 ? 'failed' : 'pending';
          
          const updated: QueuedVoiceMessage = {
            ...message,
            status: newStatus,
            retryCount: newRetryCount,
          };

          await set(`${VOICE_QUEUE_PREFIX}${message.id}`, updated);
        }
      }
    } finally {
      this.isProcessing = false;
      await this.notifyListeners();
    }
  }

  private async notifyListeners(): Promise<void> {
    const count = await this.getCount();
    this.listeners.forEach(listener => listener(count));
  }

  subscribe(listener: (count: number) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  async clearFailed(): Promise<void> {
    const all = await this.getAll();
    for (const message of all.filter(m => m.status === 'failed')) {
      await this.remove(message.id);
    }
  }
}

export const offlineVoiceQueue = new OfflineVoiceQueueService();
